import React, { useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronDown, ChevronUp, PhoneOff, Radio } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { LiveAiCall, CallSentiment } from "@/model/interface/AiAgent";
import { useAiTranscriptSocket } from "@/hooks/useAiTranscriptSocket";
import { useAiSentimentSocket } from "@/hooks/useAiSentimentSocket";
import { useGetOwnMembershipQuery, useTakeoverAiCallMutation } from "@/store/rest_api";
import { MembershipRole } from "@/enum/MembershipRole";
import { MembershipStatus } from "@/enum/MembershipStatus";
import SentimentBadge from "./SentimentBadge";
import { useCallDialog } from "@/context/CallDialogContext";
import type { Call as TwilioCall } from "@twilio/voice-sdk";
import { applyPreferredMicrophone } from "@/components/dialer/util/MicrophoneUtil";

/** Seconds elapsed, ticking every second so the card feels live. */
function useElapsed(startedAt: string): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const seconds = Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000));
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

/**
 * A single in-progress AI call: who's on it, how it's going, and the live transcript.
 *
 * The transcript combines the backfill returned by the live-calls endpoint (everything said before
 * this card mounted) with the websocket stream from that point on — without the backfill, opening a
 * call mid-way would show an empty panel until the next utterance.
 */
export default function LiveCallCard({
  call,
  expanded,
  onToggle,
}: {
  call: LiveAiCall;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { data: ownMembership } = useGetOwnMembershipQuery();
  const canTakeOver =
    ownMembership?.status === MembershipStatus.ACTIVE &&
    [MembershipRole.OWNER, MembershipRole.ADMIN, MembershipRole.AI_SUPERVISOR].includes(ownMembership.role);
  const [takeoverCall, { isLoading: isTakingOver }] = useTakeoverAiCallMutation();
  const [takeoverOpen, setTakeoverOpen] = useState(false);
  const [takeoverNumber, setTakeoverNumber] = useState("");
  // The Twilio Device is registered globally in DashboardLayout and shared via this context —
  // it's what lets us join the takeover conference without ringing anything. We also hand the
  // joined call BACK to the context so the app's normal in-call dialog owns it: the moment the AI
  // stops, this call leaves the live-calls list and this card unmounts, which would otherwise take
  // the hang-up button with it and leave audio running with no way to control it.
  const { device, setActiveCall, setActiveCallInfo, openCallDialog } = useCallDialog();
  const [joinedCall, setJoinedCall] = useState<TwilioCall | null>(null);

  const activeSid = expanded ? call.callSid : null;
  const { lines: allLines } = useAiTranscriptSocket(expanded ? call.callId : null, activeSid);
  const { latestSentiment } = useAiSentimentSocket(activeSid);

  const elapsed = useElapsed(call.startedAt);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [allLines.length, expanded]);

  const handleTakeover = async () => {
    try {
      const result = await takeoverCall({
        agentId: call.agentId,
        callId: call.callId,
        // Blank means "use the number on my profile", which the backend resolves.
        phoneNumber: takeoverNumber.trim() || undefined,
      }).unwrap();

      if (result.status === "transferring" && result.mode === "browser") {
        setTakeoverOpen(false);
        // The caller is already parked on hold music. Dial ourselves into the same conference —
        // no ringing, no answering: clicking "take over" puts us straight on the call.
        if (!device) {
          toast.error("Your browser phone isn't ready. Reload the page, or enter a number to ring.");
          return;
        }
        // Without this, an absent name is sent as the string "null" and Twilio dials nothing
        // useful — the caller sits in a conference nobody can join.
        if (!result.conferenceName) {
          toast.error("Couldn't work out which call to join. Enter a number to ring instead.");
          return;
        }
        try {
          // Named twilioCall so it doesn't shadow the LiveAiCall prop — `callId` below must be the
          // platform's call id, not the Twilio one.
          await applyPreferredMicrophone(device);
          const twilioCall = await device.connect({
            params: { conference: result.conferenceName },
          });
          setJoinedCall(twilioCall);

          // Hand it to the global call dialog and open it, so the user gets the normal in-call
          // controls that persist after this card disappears from the live-calls list.
          setActiveCall(twilioCall);
          setActiveCallInfo({
            callId: call.callId,
            displayName: who,
            contactId: null,
            startedAt: Date.now(),
          });
          openCallDialog();

          twilioCall.on("disconnect", () => {
            setJoinedCall(null);
            setActiveCall(null);
            setActiveCallInfo(null);
          });
          toast.success("You're on the call.");
        } catch {
          // The caller is on hold with the AI stopped, so say plainly what to do next. If we never
          // join, the backend's ring-window backstop puts the AI back on rather than stranding them.
          toast.error("Couldn't connect your browser — check microphone access, or ring a phone instead.");
        }
      } else if (result.status === "transferring") {
        toast.success(`Calling ${result.destination} — the caller is on hold.`);
        setTakeoverOpen(false);
      } else if (result.status === "no_destination") {
        // The call was left untouched, so the agent is still talking — say so explicitly.
        toast.error("Couldn't reach you. Enter a phone number to ring instead.");
      } else {
        toast.info("This call has already ended.");
        setTakeoverOpen(false);
      }
    } catch {
      toast.error("Failed to take over the call.");
    }
  };

  const who = call.contactName || call.phoneNumber || "Unknown caller";

  return (
    <Card className="border-l-4 border-l-red-500">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
              </span>
              <span className="font-medium truncate">{who}</span>
              <Badge variant="outline" className="text-[11px] bg-background">
                {call.direction === "OUTBOUND" ? "Outbound" : "Inbound"}
              </Badge>
              {latestSentiment?.sentiment && (
                <SentimentBadge sentiment={latestSentiment.sentiment as CallSentiment} />
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1 truncate">
              {call.agentName ?? "AI agent"} · {elapsed} elapsed
              {call.contactName && call.phoneNumber ? ` · ${call.phoneNumber}` : ""}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onToggle} className="gap-1">
              <Radio className="h-3 w-3" />
              {expanded ? "Hide" : "Listen in"}
              {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </Button>
            {joinedCall ? (
              <>
                <Badge variant="outline" className="bg-background border-green-600 text-green-700 dark:text-green-400">
                  You're on this call
                </Badge>
                <Button
                  variant="destructive"
                  size="sm"
                  className="gap-1"
                  onClick={() => {
                    // Hanging up ends the whole call — a person already took it over, so there's
                    // nothing to hand back to the AI.
                    joinedCall.disconnect();
                    setJoinedCall(null);
                  }}
                >
                  <PhoneOff className="h-3 w-3" />
                  Hang up
                </Button>
              </>
            ) : (
              canTakeOver && (
                <Button variant="destructive" size="sm" onClick={() => setTakeoverOpen(true)} className="gap-1">
                  <PhoneOff className="h-3 w-3" />
                  Take over
                </Button>
              )
            )}
          </div>
        </div>

        {expanded && (
          <div className="border rounded-lg bg-background">
            <div className="px-3 py-2 border-b text-xs font-medium text-muted-foreground">
              Live transcript
            </div>
            <div ref={scrollRef} className="max-h-72 overflow-y-auto p-3 space-y-2 text-sm">
              {allLines.length === 0 && (
                <p className="text-xs italic text-muted-foreground">Waiting for the first words…</p>
              )}
              {allLines.map((line, index) => (
                <div
                  key={index}
                  className={cn(
                    "flex gap-2",
                    line.role === "assistant"
                      ? "text-foreground"
                      : line.role === "user"
                        ? "text-primary"
                        : "text-muted-foreground",
                  )}
                >
                  <span className="text-[10px] font-mono uppercase text-muted-foreground min-w-[3.5rem] pt-0.5">
                    {line.role === "assistant" ? "Agent" : line.role === "user" ? "Caller" : line.role}
                  </span>
                  <span className="flex-1 whitespace-pre-wrap">{line.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      <Dialog open={takeoverOpen} onOpenChange={setTakeoverOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Take over this call</DialogTitle>
            <DialogDescription>
              The AI stops immediately and {who} goes on hold. Leave the field empty and you'll join
              the call right here — nothing to answer. If you don't make it onto the call, the AI
              picks the conversation back up so the caller isn't stranded.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label htmlFor="takeover-number">Ring a phone instead (optional)</Label>
            <Input
              id="takeover-number"
              type="tel"
              placeholder="Leave empty to join from this browser"
              value={takeoverNumber}
              onChange={(event) => setTakeoverNumber(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Only fill this in if you'd rather take the call on a phone. E.164 format, e.g.
              +14155551234.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTakeoverOpen(false)} disabled={isTakingOver}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleTakeover} disabled={isTakingOver}>
              {isTakingOver ? "Taking over…" : "Take over"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
